import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import { toast } from "react-toastify";
import {
  addTemplate,
  deleteTemplate,
  getTemplate,
  previewTemplate,
  sendTemplateTest,
  updateTemplate,
} from "../services/TemplateServices";
import useAuthStore from "../store/useAuthStore";
import {
  createEmptyTemplateDelta,
  getTemplateEditorValue,
  isTemplateBodyEmpty,
  parseStoredTemplateBody,
  serializeTemplateDelta,
} from "../utils/templateDelta";

const MAX_NAME_LENGTH = 100;
const MAX_SUBJECT_LENGTH = 200;
const MAX_TEST_RECIPIENTS = 5;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MERGE_FIELDS = [
  { token: "{{POC}}", label: "Contact name", sample: "Alex" },
  { token: "{{companyName}}", label: "Company", sample: "Acme Healthcare" },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }, { size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ align: [] }, "blockquote", "link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "indent",
  "align",
  "blockquote",
  "link",
];

const Icon = ({ name, className = "h-5 w-5" }) => {
  const paths = {
    plus: <path d="M12 5v14M5 12h14" />,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    template: <><path d="M6 3h9l4 4v14H6z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></>,
    edit: <><path d="m4 20 4.3-1 10.9-10.9a2.1 2.1 0 0 0-3-3L5.3 16Z" /><path d="m14.7 6.6 3 3" /></>,
    save: <><path d="M5 3h12l2 2v16H5z" /><path d="M8 3v6h8V3M8 21v-7h8v7" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></>,
    trash: <><path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14" /><path d="M10 11v6M14 11v6" /></>,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    desktop: <><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M8 22h8M12 18v4" /></>,
    mobile: <><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></>,
    eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></>,
    chevron: <path d="m9 18 6-6-6-6" />,
    check: <path d="m5 12 4 4L19 6" />,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
    refresh: <><path d="M20 7v5h-5" /><path d="M19 12a7 7 0 1 0-2 5" /></>,
  };

  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
};

const createDraft = () => {
  const editorValue = createEmptyTemplateDelta();
  return {
    id: null,
    name: "",
    subject: "",
    body: serializeTemplateDelta(editorValue),
    editorValue,
    fromEmail: "",
    isDefault: false,
    isGlobal: false,
    user: null,
  };
};

const prepareTemplate = (template) => ({
  ...template,
  editorValue: getTemplateEditorValue(template.body),
  fromEmail: template.fromEmail || "",
  isDefault: Boolean(template.isDefault),
  isGlobal: Boolean(template.isGlobal),
});

const canonicalTemplateBody = (body) => {
  const delta = parseStoredTemplateBody(body);
  if (!delta) return body || "";

  const operations = delta.ops.reduce((normalized, operation) => {
    const attributes = operation.attributes || {};
    const previous = normalized[normalized.length - 1];
    if (
      previous &&
      typeof previous.insert === "string" &&
      typeof operation.insert === "string" &&
      JSON.stringify(previous.attributes || {}) === JSON.stringify(attributes)
    ) {
      previous.insert += operation.insert;
      return normalized;
    }
    const normalizedOperation = { insert: operation.insert };
    if (Object.keys(attributes).length) {
      normalizedOperation.attributes = Object.keys(attributes)
        .sort()
        .reduce((sorted, key) => ({ ...sorted, [key]: attributes[key] }), {});
    }
    normalized.push(normalizedOperation);
    return normalized;
  }, []);

  return JSON.stringify({ ops: operations });
};

const snapshotTemplate = (template) =>
  JSON.stringify({
    id: template?.id || null,
    name: template?.name || "",
    subject: template?.subject || "",
    body: canonicalTemplateBody(template?.body),
    fromEmail: template?.fromEmail || "",
    isDefault: Boolean(template?.isDefault),
    isGlobal: Boolean(template?.isGlobal),
  });

const bodyPlainText = (body) => {
  const delta = parseStoredTemplateBody(body);
  if (delta) {
    return delta.ops
      .map((operation) =>
        typeof operation?.insert === "string" ? operation.insert : ""
      )
      .join("")
      .trim();
  }

  if (typeof body !== "string") return "";
  if (/<\/?[a-z][\s\S]*>/i.test(body) && typeof DOMParser !== "undefined") {
    return new DOMParser().parseFromString(body, "text/html").body.textContent.trim();
  }
  return body.trim();
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const Dialog = ({ title, description, onClose, children }) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/65 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-dialog-title"
        className="mobile-safe-bottom w-full rounded-t-3xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:max-w-lg sm:rounded-3xl sm:p-6"
      >
        <header className="flex items-start justify-between gap-4">
          <div>
            <h2 id="template-dialog-title" className="text-xl font-bold text-slate-950 dark:text-white">
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <Icon name="close" className="h-5 w-5" />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
};

const EditTemplatePage = () => {
  const { role, token } = useAuthStore((state) => state);
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocusedField = useRef("body");

  const [templateList, setTemplateList] = useState([]);
  const [draft, setDraft] = useState(null);
  const [originalDraft, setOriginalDraft] = useState(null);
  const [mode, setMode] = useState("view");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testRecipients, setTestRecipients] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState("write");
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const canCreate = role === "admin" || role === "sub-admin";
  const canManageDraft =
    Boolean(draft) &&
    (role === "admin" || (role === "sub-admin" && !draft.isGlobal));
  const editing = mode === "add" || mode === "edit";
  const dirty =
    editing && snapshotTemplate(draft) !== snapshotTemplate(originalDraft);

  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return templateList;
    return templateList.filter((template) =>
      [template.name, template.subject, template.user?.name]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [search, templateList]);

  const bodyText = useMemo(() => bodyPlainText(draft?.body), [draft?.body]);
  const bodyWords = bodyText ? bodyText.split(/\s+/).filter(Boolean).length : 0;

  const openTemplate = (template, nextMode = "view", force = false) => {
    if (!force && dirty && !window.confirm("Discard your unsaved changes?")) return;
    const prepared = prepareTemplate(template);
    setDraft(prepared);
    setOriginalDraft(prepared);
    setMode(nextMode);
    setWorkspaceTab("write");
  };

  const loadTemplates = async (preferredId = null) => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await getTemplate();
      const templates = response?.data?.data || [];
      setTemplateList(templates);
      const selected =
        templates.find((template) => template.id === preferredId) || templates[0];
      if (selected) openTemplate(selected, "view", true);
      else {
        setDraft(null);
        setOriginalDraft(null);
        setMode("view");
      }
      return templates;
    } catch (error) {
      setLoadError(getErrorMessage(error, "Templates could not be loaded"));
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [dirty]);

  useEffect(() => {
    if (!draft) {
      setPreview(null);
      return undefined;
    }

    let active = true;
    const timeout = window.setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError("");
      try {
        const response = await previewTemplate({
          subject: draft.subject || "Untitled email",
          body: draft.body,
        });
        if (active) setPreview(response.data);
      } catch (error) {
        if (active) {
          setPreviewError(getErrorMessage(error, "Preview is unavailable"));
        }
      } finally {
        if (active) setPreviewLoading(false);
      }
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [draft?.body, draft?.subject, draft]);

  const startNewTemplate = () => {
    if (dirty && !window.confirm("Discard your unsaved changes?")) return;
    const nextDraft = createDraft();
    setDraft(nextDraft);
    setOriginalDraft(nextDraft);
    setMode("add");
    setWorkspaceTab("write");
  };

  const beginEditing = () => {
    setMode("edit");
    window.requestAnimationFrame(() => quillRef.current?.getEditor()?.blur());
  };

  const updateDraft = (updates) =>
    setDraft((current) => ({ ...current, ...updates }));

  const validateDraft = (body) => {
    if (!draft.name.trim()) return "Template name is required";
    if (draft.name.trim().length > MAX_NAME_LENGTH) return "Template name is too long";
    if (!draft.subject.trim()) return "Subject is required";
    if (draft.subject.trim().length > MAX_SUBJECT_LENGTH) return "Subject is too long";
    if (isTemplateBodyEmpty(body)) return "Write something in the email body";
    if (draft.fromEmail && !EMAIL_REGEX.test(draft.fromEmail.trim())) {
      return "Enter a valid sender email";
    }
    return "";
  };

  const handleSave = async () => {
    if (!draft || saving) return;
    const editor = quillRef.current?.getEditor();
    const body = editor
      ? serializeTemplateDelta(editor.getContents())
      : draft.body;
    const validationError = validateDraft(body);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const payload = {
      id: draft.id,
      name: draft.name.trim(),
      subject: draft.subject.trim(),
      body,
      fromEmail: draft.fromEmail.trim(),
      isDefault: Boolean(draft.isDefault),
      isGlobal: Boolean(draft.isGlobal),
    };

    setSaving(true);
    try {
      const response = mode === "add"
        ? await addTemplate(payload)
        : await updateTemplate(payload);
      const savedId = response?.data?.template?.id || draft.id;
      toast.success(mode === "add" ? "Template created" : "Changes saved");
      await loadTemplates(savedId);
    } catch (error) {
      toast.error(getErrorMessage(error, "Template could not be saved"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft?.id || deleting) return;
    setDeleting(true);
    try {
      await deleteTemplate(draft.id);
      toast.success("Template deleted");
      setShowDeleteDialog(false);
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error, "Template could not be deleted"));
    } finally {
      setDeleting(false);
    }
  };

  const insertMergeField = (tokenValue) => {
    if (!editing) return;
    if (lastFocusedField.current === "subject" && subjectRef.current) {
      const input = subjectRef.current;
      const start = input.selectionStart ?? draft.subject.length;
      const end = input.selectionEnd ?? start;
      const subject = `${draft.subject.slice(0, start)}${tokenValue}${draft.subject.slice(end)}`;
      updateDraft({ subject });
      window.requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(start + tokenValue.length, start + tokenValue.length);
      });
      return;
    }

    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true) || { index: editor.getLength() - 1 };
    editor.insertText(range.index, tokenValue, "user");
    editor.setSelection(range.index + tokenValue.length, 0, "silent");
  };

  const parsedRecipients = () => [
    ...new Set(
      testRecipients
        .split(/[,;\n]+/)
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
    ),
  ];

  const handleSendTest = async () => {
    const recipients = parsedRecipients();
    if (!recipients.length) {
      toast.error("Add at least one recipient");
      return;
    }
    if (recipients.length > MAX_TEST_RECIPIENTS) {
      toast.error(`Use no more than ${MAX_TEST_RECIPIENTS} test recipients`);
      return;
    }
    if (recipients.some((email) => !EMAIL_REGEX.test(email))) {
      toast.error("One or more email addresses are invalid");
      return;
    }

    setSendingTest(true);
    try {
      const response = await sendTemplateTest({ templateId: draft.id, recipients });
      toast.success(response?.data?.message || "Test email sent");
      setShowTestDialog(false);
      setTestRecipients("");
    } catch (error) {
      toast.error(getErrorMessage(error, "Test email could not be sent"));
    } finally {
      setSendingTest(false);
    }
  };

  const cancelEditing = () => {
    if (mode === "add") {
      if (templateList[0]) openTemplate(templateList[0], "view", true);
      else {
        setDraft(null);
        setOriginalDraft(null);
        setMode("view");
      }
      return;
    }
    setDraft(originalDraft);
    setMode("view");
  };

  const renderTemplateSelector = (mobile = false) => {
    if (mobile) {
      return (
        <div className="lg:hidden">
          <label htmlFor="mobile-template-select" className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            Template
          </label>
          <div className="relative">
            <select
              id="mobile-template-select"
              value={draft?.id || ""}
              onChange={(event) => {
                const selected = templateList.find((item) => item.id === event.target.value);
                if (selected) openTemplate(selected);
              }}
              className="min-h-12 w-full appearance-none rounded-2xl border border-slate-200 bg-white px-4 pr-10 text-sm font-semibold text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              {mode === "add" && <option value="">New template</option>}
              {templateList.map((template) => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </select>
            <Icon name="chevron" className="pointer-events-none absolute right-3 top-3.5 h-5 w-5 rotate-90 text-slate-400" />
          </div>
        </div>
      );
    }

    return (
      <aside className="hidden self-start rounded-3xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:sticky lg:top-20 lg:block">
        <div className="p-2">
          <div className="relative">
            <Icon name="search" className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search templates"
              placeholder="Search templates"
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:focus:ring-indigo-950"
            />
          </div>
        </div>

        <div className="mt-1 max-h-[calc(100vh-16rem)] space-y-1 overflow-y-auto px-1 pb-1">
          {filteredTemplates.map((template) => {
            const active = draft?.id === template.id;
            return (
              <button
                key={template.id}
                type="button"
                onClick={() => openTemplate(template)}
                className={`w-full rounded-2xl p-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  active
                    ? "bg-indigo-50 text-indigo-950 ring-1 ring-indigo-100 dark:bg-indigo-500/15 dark:text-white dark:ring-indigo-500/30"
                    : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl ${active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"}`}>
                    <Icon name="template" className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold">{template.name}</span>
                    <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">{template.subject}</span>
                    <span className="mt-2 flex flex-wrap gap-1">
                      {template.isGlobal && <Badge tone="indigo">Workspace</Badge>}
                      {template.isDefault && <Badge tone="emerald">Default</Badge>}
                      {!template.isGlobal && role === "admin" && template.user?.name && (
                        <Badge>{template.user.name}</Badge>
                      )}
                    </span>
                  </span>
                </div>
              </button>
            );
          })}
          {!filteredTemplates.length && !loading && (
            <p className="px-3 py-8 text-center text-sm text-slate-400">No matching templates</p>
          )}
        </div>
      </aside>
    );
  };

  if (loading && !draft) {
    return <TemplatePageSkeleton />;
  }

  return (
    <main className="mx-auto w-full max-w-[1500px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-300">Communication</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">Email templates</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            Create clean, responsive emails that are ready for Gmail, Outlook, and mobile inboxes.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={startNewTemplate}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <Icon name="plus" className="h-4 w-4" /> New template
          </button>
        )}
      </header>

      {loadError && (
        <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200 sm:flex-row sm:items-center sm:justify-between">
          <span>{loadError}</span>
          <button type="button" onClick={() => loadTemplates(draft?.id)} className="inline-flex items-center gap-2 font-bold">
            <Icon name="refresh" className="h-4 w-4" /> Try again
          </button>
        </div>
      )}

      <div className="mt-6 grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
        {renderTemplateSelector(false)}

        <section className="min-w-0">
          {renderTemplateSelector(true)}

          {!draft ? (
            <EmptyTemplates canCreate={canCreate} onCreate={startNewTemplate} />
          ) : (
            <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:mt-0">
              <div className="border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-lg font-bold text-slate-950 dark:text-white sm:text-xl">
                        {mode === "add" ? "New email template" : draft.name}
                      </h2>
                      {draft.isGlobal && <Badge tone="indigo">Workspace</Badge>}
                      {draft.isDefault && <Badge tone="emerald">My default</Badge>}
                      {dirty && <Badge tone="amber">Unsaved</Badge>}
                    </div>
                    <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                      {mode === "add"
                        ? "Build a reusable message"
                        : draft.user?.name
                          ? `Owned by ${draft.user.name}`
                          : "Available across the workspace"}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {!editing && canManageDraft && (
                      <button type="button" onClick={beginEditing} className="template-secondary-button">
                        <Icon name="edit" className="h-4 w-4" /> Edit
                      </button>
                    )}
                    {canCreate && draft.id && (
                      <button
                        type="button"
                        onClick={() => setShowTestDialog(true)}
                        disabled={dirty}
                        title={dirty ? "Save changes before sending a test" : "Send a test email"}
                        className="template-secondary-button disabled:cursor-not-allowed disabled:opacity-45"
                      >
                        <Icon name="mail" className="h-4 w-4" /> Send test
                      </button>
                    )}
                    {editing && (
                      <>
                        <button type="button" onClick={cancelEditing} disabled={saving} className="template-secondary-button">Cancel</button>
                        <button type="button" onClick={handleSave} disabled={saving} className="template-primary-button">
                          <Icon name="save" className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Template name" hint={`${draft.name.length}/${MAX_NAME_LENGTH}`}>
                    <input
                      type="text"
                      value={draft.name}
                      maxLength={MAX_NAME_LENGTH}
                      onChange={(event) => updateDraft({ name: event.target.value })}
                      readOnly={!editing}
                      placeholder="e.g. Post-event follow-up"
                      className="template-input"
                    />
                  </Field>
                  <Field label="Email subject" hint={`${draft.subject.length}/${MAX_SUBJECT_LENGTH}`}>
                    <input
                      ref={subjectRef}
                      type="text"
                      value={draft.subject}
                      maxLength={MAX_SUBJECT_LENGTH}
                      onFocus={() => { lastFocusedField.current = "subject"; }}
                      onChange={(event) => updateDraft({ subject: event.target.value })}
                      readOnly={!editing}
                      placeholder="A clear subject line"
                      className="template-input"
                    />
                  </Field>
                  {role === "admin" ? (
                    <Field label="Sender email" hint="Must be allowed in Microsoft 365">
                      <input
                        type="email"
                        value={draft.fromEmail}
                        onChange={(event) => updateDraft({ fromEmail: event.target.value })}
                        readOnly={!editing}
                        placeholder="connect@cyno.co.in"
                        className="template-input"
                      />
                    </Field>
                  ) : (
                    draft.fromEmail && (
                      <Field label="Sender email" hint="Managed by the workspace admin">
                        <input type="text" value={draft.fromEmail} readOnly className="template-input" />
                      </Field>
                    )
                  )}
                  {canManageDraft && (
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Toggle
                        label="My default"
                        description="Preselect this template"
                        checked={draft.isDefault}
                        disabled={!editing}
                        onChange={(checked) => updateDraft({ isDefault: checked })}
                      />
                      {role === "admin" && (
                        <Toggle
                          label="Workspace template"
                          description="Make available across the workspace"
                          checked={draft.isGlobal}
                          disabled={!editing}
                          onChange={(checked) => updateDraft({ isGlobal: checked })}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-b border-slate-200 p-3 dark:border-slate-800 xl:hidden">
                <div className="grid grid-cols-2 rounded-xl bg-slate-100 p-1 dark:bg-slate-950">
                  <WorkspaceTab active={workspaceTab === "write"} onClick={() => setWorkspaceTab("write")} icon="edit">Write</WorkspaceTab>
                  <WorkspaceTab active={workspaceTab === "preview"} onClick={() => setWorkspaceTab("preview")} icon="eye">Preview</WorkspaceTab>
                </div>
              </div>

              <div className="grid min-w-0 xl:grid-cols-[minmax(0,1.08fr)_minmax(390px,.92fr)]">
                <div className={`${workspaceTab === "preview" ? "hidden xl:block" : "block"} min-w-0 border-slate-200 dark:border-slate-800 xl:border-r`}>
                  <div className="flex flex-col gap-3 border-b border-slate-200 p-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">Message</h3>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{bodyWords} words · {bodyText.length} characters</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5" aria-label="Merge fields">
                      {MERGE_FIELDS.map((field) => (
                        <button
                          key={field.token}
                          type="button"
                          disabled={!editing}
                          onClick={() => insertMergeField(field.token)}
                          title={`${field.label}: ${field.sample}`}
                          className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 font-mono text-[11px] font-bold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100 disabled:cursor-default disabled:opacity-55 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300 dark:hover:bg-indigo-900/60"
                        >
                          {field.token}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={`email-template-editor p-4 sm:p-6 ${editing ? "" : "is-readonly"}`}>
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      value={draft.editorValue}
                      onFocus={() => { lastFocusedField.current = "body"; }}
                      onChange={(_, __, ___, editor) => {
                        const editorValue = editor.getContents();
                        updateDraft({
                          body: serializeTemplateDelta(editorValue),
                          editorValue,
                        });
                      }}
                      readOnly={!editing}
                      placeholder="Write your email…"
                    />
                    <div className="mt-3 flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-xs leading-5 text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                      <Icon name="info" className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>Preview data: <strong>Alex</strong> at <strong>Acme Healthcare</strong>. Links support https, mailto, and tel.</span>
                    </div>
                  </div>

                  {canManageDraft && draft.id && !editing && (
                    <div className="border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
                      <button type="button" onClick={() => setShowDeleteDialog(true)} className="inline-flex min-h-10 items-center gap-2 rounded-xl px-3 text-sm font-bold text-rose-600 transition hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40">
                        <Icon name="trash" className="h-4 w-4" /> Delete template
                      </button>
                    </div>
                  )}
                </div>

                <div className={`${workspaceTab === "write" ? "hidden xl:block" : "block"} min-w-0 bg-slate-50/80 dark:bg-slate-950/60`}>
                  <PreviewPanel
                    preview={preview}
                    loading={previewLoading}
                    error={previewError}
                    device={previewDevice}
                    onDeviceChange={setPreviewDevice}
                  />
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {showTestDialog && (
        <Dialog
          title="Send a test email"
          description="Use up to five addresses. Gmail, Outlook, Microsoft 365, and other valid inboxes are supported."
          onClose={() => !sendingTest && setShowTestDialog(false)}
        >
          <div className="mt-5">
            <label htmlFor="test-recipients" className="text-sm font-bold text-slate-800 dark:text-slate-200">Recipients</label>
            <textarea
              id="test-recipients"
              rows="3"
              autoFocus
              value={testRecipients}
              onChange={(event) => setTestRecipients(event.target.value)}
              placeholder="name@gmail.com, name@outlook.com"
              className="mt-2 w-full resize-none rounded-2xl border border-slate-200 bg-white p-3 text-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:focus:ring-indigo-950"
            />
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span className="rounded-lg bg-slate-100 px-2 py-1 font-bold dark:bg-slate-800">Gmail</span>
              <span className="rounded-lg bg-slate-100 px-2 py-1 font-bold dark:bg-slate-800">Outlook</span>
              <span>Sent with sample merge-field data</span>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setShowTestDialog(false)} disabled={sendingTest} className="template-secondary-button">Cancel</button>
            <button type="button" onClick={handleSendTest} disabled={sendingTest} className="template-primary-button">
              <Icon name="mail" className="h-4 w-4" /> {sendingTest ? "Sending…" : "Send test"}
            </button>
          </div>
        </Dialog>
      )}

      {showDeleteDialog && (
        <Dialog title="Delete this template?" description={`“${draft?.name}” will be permanently removed. Campaigns using it may need a replacement.`} onClose={() => !deleting && setShowDeleteDialog(false)}>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setShowDeleteDialog(false)} disabled={deleting} className="template-secondary-button">Keep template</button>
            <button type="button" onClick={handleDelete} disabled={deleting} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 text-sm font-bold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60">
              <Icon name="trash" className="h-4 w-4" /> {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </Dialog>
      )}
    </main>
  );
};

const Badge = ({ children, tone = "slate" }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    indigo: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  };
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tones[tone]}`}>{children}</span>;
};

const Field = ({ label, hint, children }) => (
  <label className="block min-w-0">
    <span className="mb-2 flex items-center justify-between gap-2 text-xs font-bold text-slate-700 dark:text-slate-300">
      <span>{label}</span>
      {hint && <span className="truncate font-medium text-slate-400">{hint}</span>}
    </span>
    {children}
  </label>
);

const Toggle = ({ label, description, checked, disabled, onChange }) => (
  <label className={`flex min-h-[68px] items-center gap-3 rounded-2xl border border-slate-200 p-3 dark:border-slate-700 ${disabled ? "cursor-default" : "cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-700"}`}>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      className="peer sr-only"
    />
    <span className="relative h-6 w-11 shrink-0 rounded-full bg-slate-200 transition peer-checked:bg-indigo-600 peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500 peer-focus-visible:ring-offset-2 peer-disabled:opacity-60 dark:bg-slate-700">
      <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition ${checked ? "left-6" : "left-1"}`} />
    </span>
    <span className="min-w-0">
      <span className="block text-xs font-bold text-slate-800 dark:text-slate-200">{label}</span>
      <span className="mt-0.5 block text-[11px] text-slate-500 dark:text-slate-400">{description}</span>
    </span>
  </label>
);

const WorkspaceTab = ({ active, onClick, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-lg text-xs font-bold transition ${active ? "bg-white text-slate-950 shadow-sm dark:bg-slate-800 dark:text-white" : "text-slate-500 dark:text-slate-400"}`}
  >
    <Icon name={icon} className="h-4 w-4" /> {children}
  </button>
);

const PreviewPanel = ({ preview, loading, error, device, onDeviceChange }) => (
  <div className="xl:sticky xl:top-16">
    <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800 sm:px-5">
      <div>
        <p className="text-xs font-bold text-slate-900 dark:text-white">Inbox preview</p>
        <p className="mt-0.5 max-w-[220px] truncate text-[11px] text-slate-500 dark:text-slate-400">{preview?.subject || "Your subject appears here"}</p>
      </div>
      <div className="flex rounded-xl bg-slate-200/70 p-1 dark:bg-slate-800" aria-label="Preview size">
        {[
          { value: "desktop", icon: "desktop", label: "Desktop preview" },
          { value: "mobile", icon: "mobile", label: "Mobile preview" },
        ].map((option) => (
          <button
            key={option.value}
            type="button"
            aria-label={option.label}
            aria-pressed={device === option.value}
            onClick={() => onDeviceChange(option.value)}
            className={`grid h-8 w-9 place-items-center rounded-lg transition ${device === option.value ? "bg-white text-indigo-700 shadow-sm dark:bg-slate-700 dark:text-indigo-300" : "text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
          >
            <Icon name={option.icon} className="h-4 w-4" />
          </button>
        ))}
      </div>
    </div>

    <div className="min-h-[620px] overflow-auto p-3 sm:p-5">
      {error ? (
        <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-dashed border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-600 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-300">{error}</div>
      ) : (
        <div className={`relative mx-auto overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60 transition-all duration-300 dark:border-slate-700 dark:shadow-black/20 ${device === "mobile" ? "max-w-[375px]" : "max-w-[720px]"}`}>
          {loading && (
            <div className="absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden bg-indigo-100 dark:bg-indigo-950">
              <span className="block h-full w-1/2 animate-pulse bg-indigo-500" />
            </div>
          )}
          {preview?.html ? (
            <iframe
              title="Rendered email preview"
              srcDoc={preview.html}
              sandbox=""
              className="h-[580px] w-full border-0 bg-white"
            />
          ) : (
            <div className="flex h-[580px] items-center justify-center p-8 text-center text-sm text-slate-400">Start writing to see the email preview.</div>
          )}
        </div>
      )}
    </div>
  </div>
);

const EmptyTemplates = ({ canCreate, onCreate }) => (
  <div className="mt-4 flex min-h-[420px] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-900 lg:mt-0">
    <span className="grid h-14 w-14 place-items-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300"><Icon name="template" className="h-6 w-6" /></span>
    <h2 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">No email templates yet</h2>
    <p className="mt-1 max-w-sm text-sm leading-6 text-slate-500 dark:text-slate-400">{canCreate ? "Create the first reusable message for your workspace." : "A workspace admin has not shared a template yet."}</p>
    {canCreate && <button type="button" onClick={onCreate} className="template-primary-button mt-5"><Icon name="plus" className="h-4 w-4" /> Create template</button>}
  </div>
);

const TemplatePageSkeleton = () => (
  <main className="mx-auto w-full max-w-[1500px] animate-pulse px-4 py-8 sm:px-6 lg:px-8">
    <div className="h-8 w-56 rounded-lg bg-slate-200 dark:bg-slate-800" />
    <div className="mt-3 h-4 w-96 max-w-full rounded bg-slate-200 dark:bg-slate-800" />
    <div className="mt-7 grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
      <div className="hidden h-[520px] rounded-3xl bg-slate-200 dark:bg-slate-800 lg:block" />
      <div className="h-[650px] rounded-3xl bg-slate-200 dark:bg-slate-800" />
    </div>
  </main>
);

export default EditTemplatePage;
